/** `/draws` — every draw of the tenant with its §9.5 state; opens the control room of one draw. */
import { useQuery } from '@tanstack/react-query';
import type { DrawAdminDto } from '@windrop/contracts';
import { Button, useIntl } from '@windrop/ui';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, ErrorState } from '../../components/EmptyState';
import { Chip, Chips } from '../../components/FilterBar';
import { PageHeader } from '../../components/PageHeader';
import { PageSkeleton } from '../../components/PageSkeleton';
import { StatusBadge, ToneBadge, humanizeStatus } from '../../components/StatusBadge';
import { isApiError } from '../../lib/api/client';
import { drawsApi } from './api';

const VIEWS: { id: string; label: string; match: (d: DrawAdminDto) => boolean }[] = [
  { id: 'all', label: 'All draws', match: () => true },
  {
    id: 'ready',
    label: 'Waiting to run',
    match: (d) => d.status === 'COMMITTED' || d.status === 'SNAPSHOT_LOCKED',
  },
  { id: 'reveal', label: 'Revealing', match: (d) => d.status === 'ALLOCATED' || d.status === 'PUBLISHED' },
  {
    id: 'done',
    label: 'Revealed & verified',
    match: (d) => d.status === 'REVEALED' || d.status === 'VERIFIED',
  },
  { id: 'attention', label: 'Needs attention', match: (d) => d.status === 'FAILED' || d.highValue },
];

export function DrawsListPage() {
  const intl = useIntl();
  const [view, setView] = useState('all');
  const query = useQuery({
    queryKey: ['draws', 'list'],
    queryFn: () => drawsApi.list(),
    refetchInterval: 30_000,
  });
  const active = VIEWS.find((v) => v.id === view) ?? VIEWS[0]!;
  const rows = useMemo(() => (query.data?.items ?? []).filter(active.match), [query.data, active]);
  const columns: Column<DrawAdminDto>[] = [
    {
      id: 'lottery',
      header: 'Lottery',
      cell: (d) => (
        <>
          <Link to={`/draws/${d.drawId}`}>
            <strong>{d.title}</strong>
          </Link>
          <br />
          <span className="wd-muted">{humanizeStatus(d.mechanic)}</span>
        </>
      ),
      sortValue: (d) => d.title,
      filterValue: (d) => `${d.title} ${d.slug}`,
    },
    {
      id: 'status',
      header: 'Draw status',
      cell: (d) => <StatusBadge kind="DrawStatus" value={d.status} />,
      sortValue: (d) => d.status,
    },
    {
      id: 'lotteryStatus',
      header: 'Lottery',
      cell: (d) => humanizeStatus(d.lotteryStatus),
      sortValue: (d) => d.lotteryStatus,
    },
    {
      id: 'when',
      header: 'Draw / close',
      cell: (d) =>
        d.drawAt
          ? intl.formatDateTime(d.drawAt)
          : d.closesAt
            ? `closes ${intl.formatDateTime(d.closesAt)}`
            : '—',
      sortValue: (d) => d.drawAt ?? d.closesAt ?? '',
    },
    {
      id: 'winners',
      header: 'Winners',
      cell: (d) => (
        <span className="wd-tabular">
          {d.counts.winners}
          {d.counts.sponsorSamples ? ` (+${d.counts.sponsorSamples} samples)` : ''}
        </span>
      ),
      sortValue: (d) => d.counts.winners,
      align: 'end',
    },
    {
      id: 'highValue',
      header: 'Control',
      cell: (d) =>
        d.highValue ? (
          <ToneBadge tone="warning">Two operators</ToneBadge>
        ) : (
          <ToneBadge tone="neutral">Single</ToneBadge>
        ),
    },
  ];
  return (
    <>
      <PageHeader
        title="Draw control room"
        description="Pool reconciliation, seed and snapshot status, inventory checks, two-person controls, reveal rehearsal, static fallback and fairness export (§16.2)."
        breadcrumb={[{ label: 'Draw control room' }]}
      >
        {query.data ? (
          <Chips label="Views">
            {VIEWS.map((v) => (
              <Chip
                key={v.id}
                pressed={view === v.id}
                onClick={() => setView(v.id)}
                count={query.data.items.filter(v.match).length}
              >
                {v.label}
              </Chip>
            ))}
          </Chips>
        ) : null}
      </PageHeader>
      {query.isPending ? (
        <PageSkeleton label="Loading draws" tiles={0} />
      ) : query.isError ? (
        <ErrorState
          title="Draws are not available"
          body="The outcome API did not answer. Retry, or open a lottery's Draw tab."
          reference={isApiError(query.error) ? query.error.supportReference : undefined}
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}
        />
      ) : query.data.items.length === 0 ? (
        <EmptyState
          title="No draws yet"
          body="A seed commitment is created when a lottery is published; it appears here with its gates."
          action={<Button href="/lotteries">Open lotteries</Button>}
        />
      ) : (
        <DataTable
          caption="Draws"
          columns={columns}
          rows={rows}
          rowKey={(d) => d.drawId}
          filterable
          filterPlaceholder="Filter by lottery"
          initialSort={{ columnId: 'when', direction: 'desc' }}
          emptyState={
            <EmptyState
              title="No draws in this view"
              body="Switch to another view to see the rest."
              action={<Button onClick={() => setView('all')}>Show all draws</Button>}
            />
          }
          rowActions={(d) => (
            <Button href={`/draws/${d.drawId}`} variant="secondary">
              Open room
            </Button>
          )}
        />
      )}
    </>
  );
}
